import {ChangeDetectionStrategy, Component, input, output} from '@angular/core';
import {
    ReorderableRowDirective,
    ReorderableRowMove,
    ReorderableRowsDirective,
} from '@brunata-angular/ui';
import {TranslatePipe} from '@brunata-angular/util';
import {ExportNoteComponent} from '../../shared/ui/export-note/export-note.component';
import {OptionSelectComponent} from '../../shared/ui/option-select/option-select.component';
import {
    ExportColumnId,
    HeaderRowMode,
    OrderedExportColumn,
    UnitPlacement,
    headerRowOptions,
    unitPlacementOptions,
} from '../../shared/models/export-columns.model';
import {ExportColumnRowComponent} from './export-column-row.component';
import {StepCardDirective} from '../step-card/step-card.directive';
import {stepLabels} from '../step-card/step-labels.model';

export type ColumnRename = {id: ExportColumnId; header: string};

export type ColumnMove = ReorderableRowMove;

@Component({
    selector: 'app-columns-step',
    standalone: true,
    providers: [
        stepLabels(
            'customizedexports_export_step_columns_title',
            'customizedexports_export_step_columns_hint',
        ),
    ],
    hostDirectives: [{directive: StepCardDirective, inputs: ['titleKey', 'hintKey']}],
    imports: [
        ReorderableRowsDirective,
        ReorderableRowDirective,
        ExportColumnRowComponent,
        ExportNoteComponent,
        OptionSelectComponent,
        TranslatePipe,
    ],
    template: `
        <div class="columns-flow d-flex flex-column gap-3">
            <div class="border p-3">
                <div class="fw-bold">
                    {{
                        'customizedexports_export_columns_header_label'
                            | translate: 'online' : 'export'
                    }}
                </div>
                <div class="text-muted small mb-3">
                    {{
                        'customizedexports_export_columns_header_hint'
                            | translate: 'online' : 'export'
                    }}
                </div>
                <app-option-select
                    [options]="headerRowOptions"
                    [selectedId]="headerRow()"
                    name="customized-export-header-row"
                    ariaKey="customizedexports_export_columns_header_label"
                    (optionSelected)="headerRowSelected.emit($event)" />
            </div>

            <div class="border p-3">
                <div class="fw-bold">
                    {{
                        'customizedexports_export_columns_unit_label'
                            | translate: 'online' : 'export'
                    }}
                </div>
                <div class="text-muted small mb-3">
                    {{
                        'customizedexports_export_columns_unit_hint'
                            | translate: 'online' : 'export'
                    }}
                </div>
                <app-option-select
                    [options]="unitPlacementOptions"
                    [selectedId]="unitPlacement()"
                    name="customized-export-unit-placement"
                    ariaKey="customizedexports_export_columns_unit_label"
                    (optionSelected)="unitPlacementSelected.emit($event)" />
            </div>

            <div class="d-flex justify-content-end">
                <button
                    type="button"
                    class="btn btn-outline-secondary"
                    (click)="settingsReset.emit()">
                    {{ 'customizedexports_export_columns_reset' | translate: 'online' : 'export' }}
                </button>
            </div>

            <app-export-note icon="bi-info-circle-fill">
                <p class="mb-1">
                    {{
                        'customizedexports_export_columns_drag_hint'
                            | translate: 'online' : 'export'
                    }}
                </p>
                <p class="mb-0">
                    {{
                        'customizedexports_export_columns_rename_hint'
                            | translate: 'online' : 'export'
                    }}
                </p>
            </app-export-note>

            <div class="border">
                <div class="table-responsive">
                    <table class="table table-striped table-hover panel-table align-middle mb-0">
                        <thead>
                            <tr>
                                <th scope="col" class="text-nowrap text-center w-fit">
                                    {{
                                        'customizedexports_export_columns_col_order'
                                            | translate: 'online' : 'export'
                                    }}
                                </th>
                                <th scope="col" class="w-fit"></th>
                                <th scope="col">
                                    {{
                                        'customizedexports_export_columns_col_field'
                                            | translate: 'online' : 'export'
                                    }}
                                </th>
                                <th scope="col">
                                    {{
                                        'customizedexports_export_columns_col_header'
                                            | translate: 'online' : 'export'
                                    }}
                                </th>
                            </tr>
                        </thead>
                        <tbody appReorderableRows (rowMoved)="columnMoved.emit($event)">
                            @for (column of columns(); track column.id) {
                                <tr
                                    app-export-column-row
                                    appReorderableRow
                                    [column]="column"
                                    [appCheckboxAreaDisabled]="column.locked"
                                    (toggled)="columnToggled.emit(column.id)"
                                    (renamed)="
                                        columnRenamed.emit({id: column.id, header: $event})
                                    "></tr>
                            }
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    `,
    styles: `
        .columns-flow {
            --bs-border-color: rgba(var(--bs-primary-rgb), 0.15);
        }

        .w-fit {
            width: 1%;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColumnsStepComponent {
    readonly headerRow = input.required<HeaderRowMode>();
    readonly unitPlacement = input.required<UnitPlacement>();
    readonly columns = input.required<ReadonlyArray<OrderedExportColumn>>();

    readonly headerRowSelected = output<string>();
    readonly unitPlacementSelected = output<string>();
    readonly settingsReset = output<void>();
    readonly columnMoved = output<ColumnMove>();
    readonly columnToggled = output<ExportColumnId>();
    readonly columnRenamed = output<ColumnRename>();

    protected readonly headerRowOptions = headerRowOptions;
    protected readonly unitPlacementOptions = unitPlacementOptions;
}
